import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PropertyUnitSelector from './components/PropertyUnitSelector';
import MessageComposer from './components/MessageComposer';
import SentMessages from './components/SentMessages';
import PropertyDashboard from './components/PropertyDashboard';
import MaintenanceDashboard from './components/MaintenanceDashboard';
import ContractorManagement from './components/ContractorManagement';
import NotificationSettings from './components/NotificationSettings';
import MaintenanceAnalytics from './components/MaintenanceAnalytics';
import ScheduledMaintenance from './components/ScheduledMaintenance';
import LeaseManagement from './components/LeaseManagement';
import TenantManagement from './components/TenantManagement';
import TenantPortal from './components/TenantPortal';
import PropertyAdvertising from './components/PropertyAdvertising';
import PaymentSystem from './components/PaymentSystem';
import FinancialReporting from './components/FinancialReporting';
import ContractorMarketplace from './components/ContractorMarketplace';
import DocumentManagement from './components/DocumentManagement';
import EmailTemplateManager from './components/EmailTemplateManager';
import AIListingGenerator from './components/AIListingGenerator';
import ShowingScheduler from './components/ShowingScheduler';
import ShowingBooking from './components/ShowingBooking';
import ShowingBookingManage from './components/ShowingBookingManage';
import InspectionManagement from './components/InspectionManagement';
import NoticeGenerator from './components/NoticeGenerator';
import ThemeSwitcher from './components/ThemeSwitcher';
import Settings from './components/Settings';
import RentalApplicationManagement from './components/RentalApplicationManagement';
import PublicRentalApplicationForm from './components/PublicRentalApplicationForm';
import ProvideSinPage from './components/ProvideSinPage';
import ApplicationSuccess from './components/ApplicationSuccess';
import PublicSignaturePage from './components/PublicSignaturePage';
import SignatureSuccess from './components/SignatureSuccess';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  HomeWork as HomeWorkIcon,
  Message as MessageIcon,
  Build as BuildIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  BarChart as BarChartIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  Payments as PaymentsIcon,
  Description as DocumentIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarMonth as CalendarMonthIcon,
  FactCheck as FactCheckIcon,
  Description as NoticeIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  DashboardCustomize as HomeDashIcon,
  Chat as ChatIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import useAuth from './hooks/useAuth';
import LoginPage from './pages/Login';
import HomeDashboard from './components/HomeDashboard';
import AccountMenu from './components/AccountMenu';
import AppBarUserMeta from './components/AppBarUserMeta';
import BrandLockup from './components/BrandLockup';
import CompareScreeningProviders from './components/CompareScreeningProviders';
import ScreeningInvite from './components/ScreeningInvite';
import ScreeningSuccess from './components/ScreeningSuccess';
import ScreeningCancel from './components/ScreeningCancel';
import LegalPage from './components/legal/LegalPage';
import CommunicationsTab from './components/communications/CommunicationsTab';
import FinancialCenter from './components/financials/FinancialCenter';
import ExpenseLedger from './components/expenses/ExpenseLedger';
import { quickbooksLinkFrom, tabFromSearch } from './utils/tabDeepLink';
import {
  isCommunicationsBridgeEnabled,
  isExpenseCaptureEnabled,
  isFinancialsEnabled,
} from './config/featureFlags';

const drawerWidth = 240;
const HOME_TAB = 22;
const COMM_TAB = 23;
const FIN_TAB = 24;
const INSP_TAB = 25;
const EXP_TAB = 26;

const expensesEnabled = () => isFinancialsEnabled() && isExpenseCaptureEnabled();

const TAB_BY_KEY = {
  home: HOME_TAB,
  properties: 0,
  messaging: 1,
  maintenance: 2,
  notifications: 4,
  leases: 7,
  tenants: 8,
  documents: 14,
  settings: 21,
  communications: COMM_TAB,
  financials: FIN_TAB,
  expenses: EXP_TAB,
  inspections: INSP_TAB,
  showings: 18,
  'scheduled-maintenance': 6,
};

const menuItems = [
  { label: 'Home', icon: <HomeDashIcon />, tab: HOME_TAB },
  { label: 'Properties & Units', icon: <HomeWorkIcon />, tab: 0 },
  { label: 'Messaging', icon: <MessageIcon />, tab: 1 },
  { label: 'Communications', icon: <ChatIcon />, tab: COMM_TAB },
  { label: 'Maintenance', icon: <BuildIcon />, tab: 2 },
  { label: 'Contractors', icon: <PeopleIcon />, tab: 3 },
  { label: 'Notifications', icon: <NotificationsIcon />, tab: 4 },
  { label: 'Analytics', icon: <BarChartIcon />, tab: 5 },
  { label: 'Scheduled Maintenance', icon: <EventIcon />, tab: 6 },
  { label: 'Lease Management', icon: <DescriptionIcon />, tab: 7 },
  { label: 'Tenant Management', icon: <PersonIcon />, tab: 8 },
  { label: 'Tenant Portal', icon: <DashboardIcon />, tab: 9 },
  { label: 'Property Advertising', icon: <HomeIcon />, tab: 10 },
  { label: 'Payment System', icon: <PaymentIcon />, tab: 11 },
  { label: 'Financial Reporting', icon: <AssessmentIcon />, tab: 12 },
  { label: 'Financial Center', icon: <AccountBalanceIcon />, tab: FIN_TAB },
  { label: 'Expenses', icon: <PaymentsIcon />, tab: EXP_TAB },
  { label: 'Contractor Marketplace', icon: <BuildIcon />, tab: 13 },
  { label: 'Documents', icon: <DocumentIcon />, tab: 14 },
  { label: 'Email Templates', icon: <EmailIcon />, tab: 16 },
  { label: 'AI Listing Generator', icon: <AutoAwesomeIcon />, tab: 17 },
  { label: 'Showing Scheduler', icon: <CalendarMonthIcon />, tab: 18 },
  { label: 'Inspections', icon: <FactCheckIcon />, tab: INSP_TAB },
  { label: 'Notice Generator', icon: <NoticeIcon />, tab: 19 },
  { label: 'Rental Applications', icon: <AssignmentIcon />, tab: 20 },
  { label: 'Settings', icon: <SettingsIcon />, tab: 21 },
];

function RequireAuth({ children }) {
  const { isAuthenticated, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6">Loading session...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function DashboardShell() {
  const { user, logout, initialized } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // All hooks must be called before any conditional returns
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () =>
      tabFromSearch(location.search, TAB_BY_KEY) ??
      (quickbooksLinkFrom(location.pathname, location.search) ? FIN_TAB : HOME_TAB)
  );
  const [documentsFocus, setDocumentsFocus] = useState(null);
  const [finStartView, setFinStartView] = useState(() =>
    quickbooksLinkFrom(location.pathname, location.search) ? 'quickbooks' : null
  );
  const [quickbooksLink, setQuickbooksLink] = useState(() => quickbooksLinkFrom(location.pathname, location.search));

  useEffect(() => {
    const linkedTab = tabFromSearch(location.search, TAB_BY_KEY);
    if (linkedTab === null) return;
    setActiveTab(linkedTab);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const link = quickbooksLinkFrom(location.pathname, location.search);
    if (!link) return;
    setQuickbooksLink(link);
    setFinStartView('quickbooks');
    setActiveTab(FIN_TAB);
    navigate('/', { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (activeTab !== 14) setDocumentsFocus(null);
  }, [activeTab]);

  const openDocumentsFor = (entityType, entityId) => {
    setDocumentsFocus({ entityType, entityId, key: Date.now() });
    setActiveTab(14);
  };

  const goToTab = (target) => {
    if (typeof target === 'number') {
      setActiveTab(target);
      return;
    }
    const mapped = TAB_BY_KEY[target];
    if (mapped !== undefined) setActiveTab(mapped);
  };
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filter menu items based on role
  const getFilteredMenuItems = () => {
    if (!user) return [];
    
    // Admin sees everything (feature-flagged items gated)
    const commEnabled = isCommunicationsBridgeEnabled();
    const finEnabled = isFinancialsEnabled();
    const visible = menuItems.filter((item) => {
      if (item.tab === COMM_TAB && !commEnabled) return false;
      if (item.tab === FIN_TAB && !finEnabled) return false;
      if (item.tab === EXP_TAB && !expensesEnabled()) return false;
      return true;
    });

    if (user.role === 'Admin') {
      return visible;
    }
    
    // PropertyManager sees most items but not admin-only features
    if (user.role === 'PropertyManager') {
      return visible.filter(item => {
        // PropertyManagers don't need to see "Tenant Portal" (that's for tenants)
        return item.tab !== 9; // Tenant Portal tab
      });
    }
    
    return [];
  };

  const filteredMenuItems = getFilteredMenuItems();

  // Ensure activeTab is valid for current role
  useEffect(() => {
    if (!user || filteredMenuItems.length === 0) return;
    const validTabs = filteredMenuItems.map(item => item.tab);
    if (!validTabs.includes(activeTab) && filteredMenuItems.length > 0) {
      setActiveTab(filteredMenuItems[0].tab);
    }
  }, [user?.role, filteredMenuItems, activeTab, user]);

  // Show loading if auth is still initializing or user is not loaded
  if (!initialized || !user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Role-based routing: Tenants and Contractors get limited views
  if (user?.role === 'Tenant') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              ZenRentFlow
            </Typography>
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {user.displayName || user.email}
                </Typography>
                <Typography variant="caption" color="secondary">
                  {user.role || 'Unknown'}
                </Typography>
              </Box>
            )}
            <Button color="inherit" onClick={logout}>
              Sign Out
            </Button>
          </Toolbar>
        </AppBar>
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <TenantPortal />
        </Box>
      </Box>
    );
  }

  // Contractors get a simplified view (can expand later)
  if (user?.role === 'Contractor') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              ZenRentFlow
            </Typography>
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {user.displayName || user.email}
                </Typography>
                <Typography variant="caption" color="secondary">
                  {user.role || 'Unknown'}
                </Typography>
              </Box>
            )}
            <Button color="inherit" onClick={logout}>
              Sign Out
            </Button>
          </Toolbar>
        </AppBar>
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <MaintenanceDashboard />
        </Box>
      </Box>
    );
  }

  // Admin and PropertyManager get the full dashboard

  const drawer = (
    <div>
      <Toolbar>
        <BrandLockup variant="drawer" />
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem
            key={item.label}
            selected={activeTab === item.tab}
            onClick={() => {
              setActiveTab(item.tab);
              setFinStartView(null);
              if (isMobile) setMobileOpen(false);
            }}
            data-testid={
              item.label === 'Documents'
                ? 'nav-documents'
                : `nav-${String(item.label).toLowerCase().replace(/\s+/g, '-')}`
            }
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Routes>
      <Route path="/screening/compare" element={<CompareScreeningProviders />} />
      {/* Main App Route */}
      <Route path="/*" element={
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 }, gap: 0.5 }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 0.5 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                <BrandLockup variant="appBar" />
              </Box>
              <AppBarUserMeta user={user} />
              <ThemeSwitcher />
              <AccountMenu user={user} onLogout={logout} onNavigateTab={goToTab} />
            </Toolbar>
          </AppBar>
          <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            aria-label="sidebar navigation"
          >
            <Drawer
              variant={isMobile ? 'temporary' : 'permanent'}
              open={isMobile ? mobileOpen : true}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                display: { xs: 'block', sm: 'block' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                },
              }}
            >
              {drawer}
            </Drawer>
          </Box>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 1, sm: 2, md: 3 },
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              mt: 8,
              overflowX: 'hidden',
              boxSizing: 'border-box',
              maxWidth: '100%'
            }}
          >
            {activeTab === HOME_TAB && <HomeDashboard onNavigate={goToTab} />}
            {activeTab === 0 && (
              <PropertyDashboard onOpenDocuments={(id) => openDocumentsFor('property', id)} />
            )}
            {activeTab === 1 && (
              <>
                <Box sx={{ mb: 3 }}>
                  <PropertyUnitSelector
                    onSelect={(propId, unitId) => {
                      setPropertyId(propId);
                      setUnitId(unitId);
                    }}
                  />
                  {propertyId && unitId ? (
                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500 }}>
                      📍 Filtering messages for selected property/unit. Clear to view all messages.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      📨 Showing all messages. Select property/unit above to compose new messages.
                    </Typography>
                  )}
                </Box>
                <MessageComposer propertyId={propertyId} unitId={unitId} />
                <SentMessages propertyId={propertyId} unitId={unitId} />
              </>
            )}
            {activeTab === 2 && <MaintenanceDashboard />}
            {activeTab === 3 && <ContractorManagement />}
            {activeTab === 4 && <NotificationSettings />}
            {activeTab === 5 && <MaintenanceAnalytics />}
            {activeTab === 6 && <ScheduledMaintenance />}
            {activeTab === 7 && (
              <LeaseManagement
                onGoToFinancials={() => setActiveTab(FIN_TAB)}
                onOpenDocuments={(leaseId) => openDocumentsFor('lease', leaseId)}
              />
            )}
            {activeTab === 8 && (
              <TenantManagement
                onGoToFinancials={() => setActiveTab(FIN_TAB)}
                onOpenDocuments={(id) => openDocumentsFor('tenant', id)}
              />
            )}
            {activeTab === 9 && <TenantPortal />}
            {activeTab === 10 && <PropertyAdvertising />}
            {activeTab === 11 && <PaymentSystem />}
            {activeTab === 12 && <FinancialReporting />}
            {activeTab === 13 && <ContractorMarketplace />}
            {activeTab === 14 && <DocumentManagement focus={documentsFocus} />}
            {activeTab === 16 && <EmailTemplateManager />}
            {activeTab === 17 && <AIListingGenerator />}
            {activeTab === 18 && <ShowingScheduler />}
            {activeTab === INSP_TAB && <InspectionManagement />}
            {activeTab === 19 && <NoticeGenerator />}
            {activeTab === 20 && <RentalApplicationManagement />}
            {activeTab === COMM_TAB && isCommunicationsBridgeEnabled() && (
              <CommunicationsTab />
            )}
            {activeTab === FIN_TAB && isFinancialsEnabled() && (
              <FinancialCenter
                initialView={finStartView}
                quickbooksLink={quickbooksLink}
                onQuickbooksLinkUsed={() => setQuickbooksLink(null)}
              />
            )}
            {activeTab === EXP_TAB && expensesEnabled() && (
              <ExpenseLedger
                onOpenReceipts={() => {
                  setFinStartView('receipts');
                  setActiveTab(FIN_TAB);
                }}
              />
            )}
            {activeTab === 21 && <Settings />}
          </Box>
        </Box>
      } />
    </Routes>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/book-showing" element={<ShowingBooking />} />
      <Route path="/book-showing/manage/:token" element={<ShowingBookingManage />} />
      <Route path="/apply" element={<PublicRentalApplicationForm />} />
      <Route path="/apply/:propertyId/:unitId" element={<PublicRentalApplicationForm />} />
      <Route path="/application-success" element={<ApplicationSuccess />} />
      <Route path="/provide-sin/:token" element={<ProvideSinPage />} />
      <Route path="/sign/:signatureRequestId" element={<PublicSignaturePage />} />
      <Route path="/signature-success" element={<SignatureSuccess />} />
      <Route path="/screening/invite" element={<ScreeningInvite />} />
      <Route path="/screening/success" element={<ScreeningSuccess />} />
      <Route path="/screening/cancel" element={<ScreeningCancel />} />
      <Route path="/privacy" element={<LegalPage document="privacy" />} />
      <Route path="/terms" element={<LegalPage document="terms" />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <DashboardShell />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

export default App;
